import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BrandImage,
  deleteBrandImage,
  getBrandImagery,
  patchBrandImagery,
  scrapeBrandImagery,
} from '@/lib/api'

/**
 * Brand imagery review — scrapes the brand's homepage for promo / lifestyle
 * photography and lets the user curate (keep / delete) the set. Shared by the
 * /brand page section and the brand-setup wizard step. Self-manages all API
 * calls; the kept set is what a later phase turns into the merged Brand DNA
 * recipe (font / color / spacing / styling) attached to render prompts.
 *
 * Keep is a soft toggle (dim when off, still stored so it can be re-kept);
 * delete is a hard remove (entry + GCS object). Persistence degrades gracefully
 * — if the brands.imagery column isn't migrated yet, everything works in-memory
 * and a small notice explains that changes won't survive a reload.
 */

interface Props {
  /** Auto-run a scrape on mount when there are no images yet (wizard step). */
  autoScrapeOnMount?: boolean
  /** Fires whenever the image set changes (wizard uses it to gate Continue). */
  onChange?: (images: BrandImage[]) => void
  /** Tighter spacing for the wizard vs the roomier /brand card. */
  compact?: boolean
}

const KIND_LABEL: Record<string, string> = {
  editorial: 'Campaign',
  lifestyle: 'Lifestyle',
  model: 'Model',
  scene: 'Scene',
  unclassified: 'Photo',
}

export function BrandImageryReview({ autoScrapeOnMount, onChange, compact }: Props) {
  const [images, setImages] = useState<BrandImage[]>([])
  const [loading, setLoading] = useState(true)
  const [scraping, setScraping] = useState(false)
  const [persisted, setPersisted] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const autoRan = useRef(false)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const emit = useCallback((imgs: BrandImage[]) => {
    onChangeRef.current?.(imgs)
  }, [])

  const runScrape = useCallback(async () => {
    setScraping(true)
    setError(null)
    try {
      const res = await scrapeBrandImagery({ limit: 10 })
      setImages(res.images)
      setPersisted(res.persisted)
      emit(res.images)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scrape failed')
    } finally {
      setScraping(false)
    }
  }, [emit])

  // Initial load (+ optional auto-scrape when empty, for the wizard).
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await getBrandImagery()
        if (!alive) return
        setImages(res.images)
        setPersisted(res.persisted)
        emit(res.images)
        if (autoScrapeOnMount && !autoRan.current && res.images.length === 0) {
          autoRan.current = true
          void runScrape()
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [autoScrapeOnMount, emit, runScrape])

  // Keep toggle. UI state is authoritative; each PATCH sends the FULL kept set,
  // so out-of-order responses can't clobber the grid (we only read `persisted`).
  const toggleKeep = useCallback((id: string) => {
    setImages((prev) => {
      const next = prev.map((im) => (im.id === id ? { ...im, kept: !im.kept } : im))
      emit(next)
      const keptIds = next.filter((im) => im.kept).map((im) => im.id)
      void patchBrandImagery(keptIds)
        .then((r) => setPersisted(r.persisted))
        .catch(() => {})
      return next
    })
  }, [emit])

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const next = prev.filter((im) => im.id !== id)
      emit(next)
      return next
    })
    void deleteBrandImage(id)
      .then((r) => setPersisted(r.persisted))
      .catch(() => {})
  }, [emit])

  const keptCount = images.filter((im) => im.kept).length
  const gap = compact ? 'gap-2.5' : 'gap-3'

  return (
    <div className="space-y-4">
      {/* Header: count + scrape action */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-dark-muted">
            {loading
              ? 'Loading…'
              : images.length === 0
                ? 'No brand images yet.'
                : `${keptCount} of ${images.length} kept for your Brand DNA`}
          </p>
        </div>
        <button
          type="button"
          onClick={runScrape}
          disabled={scraping || loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-semibold shadow-md shadow-primary/25 hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {scraping ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Scanning your site…
            </>
          ) : (
            <>
              <i className={images.length === 0 ? 'ri-sparkling-2-line' : 'ri-refresh-line'} />
              {images.length === 0 ? 'Scan my site' : 'Rescan'}
            </>
          )}
        </button>
      </div>

      {!persisted && images.length > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          Changes here won't survive a reload until the brand-imagery storage is
          enabled — everything else works normally.
        </div>
      )}

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-600">
          {error}
        </div>
      )}

      {/* Grid / empty state */}
      {loading || (scraping && images.length === 0) ? (
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 ${gap}`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-surface animate-pulse" />
          ))}
        </div>
      ) : images.length === 0 ? (
        <div className="rounded-xl border border-dashed border-dark-muted/30 px-4 py-8 text-center">
          <i className="ri-image-2-line text-2xl text-dark-muted/50" />
          <p className="mt-2 text-sm text-dark-muted">
            Scan your homepage to pull in your campaign + lifestyle photography.
          </p>
        </div>
      ) : (
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 ${gap}`}>
          {images.map((im) => (
            <BrandImageCard
              key={im.id}
              image={im}
              onToggleKeep={() => toggleKeep(im.id)}
              onDelete={() => removeImage(im.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function BrandImageCard({
  image,
  onToggleKeep,
  onDelete,
}: {
  image: BrandImage
  onToggleKeep: () => void
  onDelete: () => void
}) {
  const kindLabel = KIND_LABEL[image.kind] || 'Photo'
  return (
    <div
      className={`group relative rounded-xl overflow-hidden border-2 transition-all ${
        image.kept
          ? 'border-primary shadow-md shadow-primary/15'
          : 'border-surface-dark/30'
      }`}
    >
      {/* Click the image to toggle keep */}
      <button
        type="button"
        onClick={onToggleKeep}
        className="block w-full aspect-square bg-surface"
        title={image.kept ? 'Kept — click to set aside' : 'Set aside — click to keep'}
      >
        <img
          src={image.url}
          alt=""
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity ${
            image.kept ? '' : 'opacity-35 grayscale'
          }`}
        />
      </button>

      {/* Keep badge (top-right) */}
      <button
        type="button"
        onClick={onToggleKeep}
        className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center shadow transition-colors ${
          image.kept
            ? 'bg-primary text-white'
            : 'bg-white/90 text-dark-muted hover:text-dark'
        }`}
        title={image.kept ? 'Kept' : 'Set aside'}
      >
        <i className={`text-[13px] ${image.kept ? 'ri-check-line' : 'ri-add-line'}`} />
      </button>

      {/* Delete (top-left, hover) */}
      <button
        type="button"
        onClick={onDelete}
        className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full bg-white/90 text-dark-muted flex items-center justify-center shadow opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white transition-all"
        title="Remove"
      >
        <i className="ri-delete-bin-line text-[12px]" />
      </button>

      {/* Kind + campaign-text chip (bottom) */}
      <div className="absolute bottom-0 inset-x-0 flex items-center gap-1 px-1.5 py-1 bg-gradient-to-t from-black/55 to-transparent pointer-events-none">
        <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
          {kindLabel}
        </span>
        {image.hasText && (
          <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-primary/80 rounded px-1 py-0.5">
            <i className="ri-font-size-2 text-[9px]" />
            TEXT
          </span>
        )}
      </div>
    </div>
  )
}
